/**
 * Group check-in on the rig (2026-09-28): the pieces the rig console's armed
 * screen (RigScreenV2) and the QR Reader share.
 *
 * Ivin: "We would need to add multiple QR codes before the capture to make it
 * a group. Otherwise it just goes to the owner of the single QR code." The
 * first person is armed the usual way; while the rig is armed and Start has
 * not been pressed, more people join the same capture (addGroupMember, by
 * pass or roster pick) or leave it (removeGroupMember). The server holds the
 * group on captureState.groupMembers next to the lead, locks it when Start is
 * pressed, and records everyone on the capture. One person on the rig looks
 * and works as before: no group line until a second person joins.
 *
 * Pure helpers and one presentational list, exported for the tests. Nothing
 * here writes. A group's retake ("Capture group again") is one server call,
 * armGroup, which arms everyone or nobody.
 */
import React from "react";
import "./rigv2-group.css";

// Mirrors functions/src/services/groupCheckin.ts (maxGroupSizeFor): the most
// people one capture holds, the lead included. The server decides; the screen
// only uses it to turn "Add person" off at the limit.
export const DEFAULT_MAX_GROUP_SIZE = 8;
export const MAX_GROUP_SIZE_CAP = 20;

export function maxGroupSizeOf(job) {
  const v = job?.ingestSettings?.maxGroupSize;
  return Number.isInteger(v) && v >= 1 && v <= MAX_GROUP_SIZE_CAP ? v : DEFAULT_MAX_GROUP_SIZE;
}

const initials = (name) =>
  (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join("") || "?";

export const firstName = (name) => String(name ?? "").trim().split(/\s+/)[0] || "";

// groupMembers as RTDB hands it to the browser: an array for the dense list
// the server writes (an object with numeric keys is read in key order), only
// entries with a roster id.
export function groupMembersOf(state) {
  const raw = state?.groupMembers;
  let list = [];
  if (Array.isArray(raw)) list = raw;
  else if (raw && typeof raw === "object") {
    list = Object.keys(raw).filter((k) => /^\d+$/.test(k)).sort((a, b) => Number(a) - Number(b)).map((k) => raw[k]);
  }
  return list.filter((m) => m && typeof m.rosterEntryId === "string" && m.rosterEntryId);
}

// Everyone on the arm, the lead first: { rosterEntryId, name, displayId, lead }.
export function groupPeopleOf(state) {
  if (!state?.rosterEntryId) return [];
  const lead = {
    rosterEntryId: state.rosterEntryId,
    name: state.subjectName || state.subjectDisplayId || "",
    displayId: state.subjectDisplayId || "",
    lead: true,
  };
  const members = groupMembersOf(state)
    .filter((m) => m.rosterEntryId !== lead.rosterEntryId)
    .map((m) => ({ rosterEntryId: m.rosterEntryId, name: m.subjectName || m.subjectDisplayId || "", displayId: m.subjectDisplayId || "", lead: false }));
  return [lead, ...members];
}

// "Group of 3" once a second person joins; nothing for one person.
export function groupCountLabel(n) {
  return n >= 2 ? `Group of ${n}` : "";
}

// The group can change: armed, a lease to change it on, Start not pressed.
export function groupOpen(state) {
  return state?.phase === "armed" && !state?.groupLocked && !!state?.leaseId;
}

// "Leo Park and Sam Lee", "Leo Park, Sam Lee and Ana Ruiz".
export function joinNames(names) {
  const list = names.filter(Boolean);
  if (list.length <= 1) return list[0] || "";
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}

// Why this roster entry cannot join the group on the rig, or null when it can.
// The consent part mirrors the server (functions/src/services/groupCheckin.ts
// groupEligibilityProblem), which is the one that decides.
export function groupJoinProblem(entry, state) {
  if (!entry) return null;
  if (entry.id && entry.id === state?.rosterEntryId) return "lead";
  if (entry.id && groupMembersOf(state).some((m) => m.rosterEntryId === entry.id)) return "member";
  const pf = entry.personFields || {};
  if (pf.consent?.accepted !== true) return "no_consent";
  if (pf.isMinor === true && pf.guardianConsent !== true) return "minor_without_guardian";
  return null;
}

export function groupJoinProblemText(problem, name) {
  const who = String(name ?? "").trim() || "This person";
  switch (problem) {
    case "lead": return `${who} is the first person in this capture already.`;
    case "member": return `${who} is in this capture already.`;
    case "no_consent": return `${who} has no registration consent on record, so they can't join a group.`;
    case "minor_without_guardian": return `${who} is under 18 and has no guardian consent on record.`;
    default: return "";
  }
}

// The people a finished capture belonged to: its members (lead first) when
// the attempt records them, else its lead alone (an attempt from before group
// check-in). { rosterEntryId, name }.
export function attemptPeople(attempt) {
  const members = Array.isArray(attempt?.members) ? attempt.members : [];
  const out = [];
  for (const m of members) {
    if (!m?.rosterEntryId || out.some((p) => p.rosterEntryId === m.rosterEntryId)) continue;
    out.push({ rosterEntryId: m.rosterEntryId, name: (m.displayNameAtFire || m.subjectName || "").trim() });
  }
  if (out.length) return out;
  const s = attempt?.subjectSnapshot;
  return s?.rosterEntryId ? [{ rosterEntryId: s.rosterEntryId, name: (s.displayNameAtFire || s.subjectName || "").trim() }] : [];
}

/**
 * The group on the rig: one row per person, the lead marked "first", and a
 * remove button on every other row (onRemove). badgeFor(person) may return
 * extra markup for a row (the console's consent and minor badges).
 * disabled turns every remove button off (preview, locked group, a call out).
 */
export function GroupList({ people, onRemove = null, removingId = null, disabled = false, badgeFor = null }) {
  if (!people?.length) return null;
  return (
    <ul className="rigv2-group-list" aria-label={groupCountLabel(people.length) || "Capture"}>
      {people.map((p) => (
        <li key={p.rosterEntryId} className="rigv2-group-row">
          <span className="rigv2-roster-av">{initials(p.name)}</span>
          <span className="rigv2-group-body">
            <span className="rigv2-group-name">
              {p.name || p.displayId || "Unnamed"}
              {p.name && p.displayId ? <span className="rigv2-roster-num">{p.displayId}</span> : null}
            </span>
            {badgeFor ? badgeFor(p) : null}
          </span>
          {p.lead ? (
            <span className="rigv2-group-first">first</span>
          ) : onRemove ? (
            <button type="button" className="rigv2-group-remove" disabled={disabled || !!removingId}
              aria-label={`Take ${p.name || p.displayId || "this person"} out of the capture`}
              onClick={() => onRemove(p)}>
              {removingId === p.rosterEntryId ? "…" : "×"}
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

// What "Capture again" on a recent card arms: the whole group in one step
// (armGroup) when the capture recorded more than one person with its lead
// first, else the lead alone through the live arm path; null when there is
// nobody to arm (a nameless walk-up with no roster entry).
export function againRequest(attempt) {
  const lead = attempt?.subjectSnapshot?.rosterEntryId;
  if (!lead) return null;
  const people = attemptPeople(attempt);
  if (people.length > 1 && people[0].rosterEntryId === lead) {
    return { kind: "group", rosterEntryIds: people.map((p) => p.rosterEntryId) };
  }
  return { kind: "one", rosterEntryId: lead };
}

// The console's Start (button and Space/Enter): not while Start or Cancel is
// out, not while a group change from this screen is out (the person being
// added would miss the capture), not in the preview.
export function startAllowed({ busy = null, groupBusy = null, interactive = true } = {}) {
  return interactive && !busy && !groupBusy;
}

// "Scan a pass" from the console asked the QR Reader to add to one arm
// (want: { jobId, leaseId }). The reader enters add mode for it only when
// the live state is that same arm, still open; returns the lease, or null.
export function addModeFor(want, { state, jobId, interactive = true } = {}) {
  if (!want || !interactive || !state) return null;
  if (want.jobId !== jobId || !groupOpen(state) || state.leaseId !== want.leaseId) return null;
  return want.leaseId;
}
